import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/** Matches interactive targets across home + inner pages + Bootstrap modals */
const INTERACTIVE_SELECTOR = [
  "a[href]",
  "button",
  '[role="button"]',
  "input",
  "select",
  "textarea",
  "label",
  ".project-card",
  ".service-item",
  ".rs-testimonial-card",
  ".rs-inner-btn",
  ".rs-btn-primary",
  ".rs-btn-ghost",
  ".rs-svc-hero-thumb",
  ".rs-testimonial-arrow",
  ".nav-cta",
  ".footer-text-btn",
  ".slick-arrow",
  ".rs-lc-option",
  ".modal button",
  ".modal a",
  ".modal input",
  ".modal select",
  ".modal textarea",
  ".modal label",
  ".btn-close",
  ".rs-svc-btn-outline-light",
  ".rs-about-btn-primary",
  ".rs-proj-cta-btn",
  ".rs-proj-filter-btn",
].join(", ");

/**
 * Custom cursor for all `LandingLayout` routes; portaled to `body` so it paints above modal backdrops.
 */
export default function LandingCustomCursor() {
  const cursorRef = useRef(null);
  const ringRef = useRef(null);
  const mx = useRef(0);
  const my = useRef(0);
  const rx = useRef(0);
  const ry = useRef(0);
  const rafRef = useRef(null);
  const lastInteractive = useRef(false);

  useEffect(() => {
    document.body.classList.add("refined-landing-active");
    return () => document.body.classList.remove("refined-landing-active");
  }, []);

  useEffect(() => {
    const cursor = cursorRef.current;
    const ring = ringRef.current;
    if (!cursor || !ring) return undefined;

    const onEnter = () => {
      cursor.style.width = "18px";
      cursor.style.height = "18px";
      ring.style.width = "56px";
      ring.style.height = "56px";
      ring.style.opacity = "0.3";
    };

    const onLeave = () => {
      cursor.style.width = "10px";
      cursor.style.height = "10px";
      ring.style.width = "36px";
      ring.style.height = "36px";
      ring.style.opacity = "0.6";
    };

    const onMove = (e) => {
      mx.current = e.clientX;
      my.current = e.clientY;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const hit = el?.closest(INTERACTIVE_SELECTOR);
      const isOver = Boolean(hit);
      if (isOver !== lastInteractive.current) {
        lastInteractive.current = isOver;
        if (isOver) onEnter();
        else onLeave();
      }
    };

    const animCursor = () => {
      cursor.style.left = `${mx.current}px`;
      cursor.style.top = `${my.current}px`;
      rx.current += (mx.current - rx.current) * 0.12;
      ry.current += (my.current - ry.current) * 0.12;
      ring.style.left = `${rx.current}px`;
      ring.style.top = `${ry.current}px`;
      rafRef.current = requestAnimationFrame(animCursor);
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    rafRef.current = requestAnimationFrame(animCursor);

    return () => {
      document.removeEventListener("mousemove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <div className="rs-cursor" ref={cursorRef} aria-hidden />
      <div className="rs-cursor-ring" ref={ringRef} aria-hidden />
    </>,
    document.body
  );
}
